import React from "react";
import "./GetStarted.css";
import "./HelpSupport.css";

export default function GettingStarted({ onBack }) {
  return (
    <div className="getting-started-page">
      <header className="getting-started-page__header">
        <button
          className="getting-started-page__back-btn"
          onClick={onBack}
          aria-label="Go back"
        >
          <span className="getting-started-page__back-arrow"></span>
        </button>
        <h2 className="getting-started-page__title">Getting Started</h2>
      </header>

      <p className="getting-started-page__subtitle">
        Get started with project setup, configuration, and navigation.
      </p>

      {/* ========== 1 ========== */}
      <section className="guide-section">
        <div className="guide-section__heading">
          <span className="guide-section__badge">1</span>
          <span className="guide-section__heading-text">
            INPUTTING PROJECT DATA
          </span>
        </div>

        <h4 className="guide-section__subtitle">Accessing Your Active Project</h4>
        <ul className="guide-list">
          <li>
            Open the left-hand sidebar and select Active Project under Project
            Management.
          </li>
          <li>
            If no project is active, click <b>New Project</b> to start one. Enter
            a project name, select your region, and define the analysis type
            (e.g., traffic, emissions, or mobility patterns).
          </li>
          <li>Use <b>Switch Project</b> to toggle between saved projects.</li>
        </ul>

        <h4 className="guide-section__subtitle">
          Adding Traffic and Transport Inputs
        </h4>
        <ul className="guide-list">
          <li>
            Once inside your active project, you can begin configuring key
            inputs.
          </li>
          <li>
            Use the green quick-access buttons at the bottom of the map
            interface to add:
            <ul className="guide-list guide-list--nested">
              <li>
                <b>Traffic Density:</b> Input average or real-time traffic data.
                Adjust parameters like vehicle speed, flow rate, or congestion
                levels.
              </li>
              <li>
                <b>Public Transport:</b> Include routes, stops, and ridership
                data for buses, trains, or other modes.
              </li>
              <li>
                <b>Accidents:</b> Add historical or hypothetical accident data
                to simulate disruptions or scenario impacts.
              </li>
            </ul>
          </li>
        </ul>
      </section>

      {/* ========== 2 ========== */}
      <section className="guide-section">
        <div className="guide-section__heading">
          <span className="guide-section__badge">2</span>
          <span className="guide-section__heading-text">
            UPLOADING RELEVANT FILES IN THE DATA SETS SECTION
          </span>
        </div>

        <h4 className="guide-section__subtitle">Accessing Data Sets</h4>
        <ul className="guide-list">
          <li>
            From the sidebar, navigate to <b>Resource Access ▸ Data Sets</b>.
          </li>
          <li>
            This section stores all imported and system-generated datasets for
            your current project.
          </li>
        </ul>

        <h4 className="guide-section__subtitle">Uploading New Files</h4>
        <ol className="guide-olist">
          <li>
            Click <b>Upload Dataset</b> or drag and drop your file into the
            upload panel.
          </li>
          <li>Supported formats include CSV, XLSX, and GeoJSON for spatial data.</li>
          <li>
            Ensure your file headers match the required schema (e.g.,
            “vehicle_type,” “count,” “speed,” “timestamp”).
          </li>
          <li>
            Once uploaded, the platform validates the file. Any missing fields or
            formatting issues are highlighted with error messages or correction
            suggestions.
          </li>
          <li>
            After a successful upload, your dataset appears in the list view with
            options to:
            <ul className="guide-list guide-list--nested">
              <li>Preview or filter data</li>
              <li>Rename or categorize the dataset</li>
              <li>Toggle its visibility in the project map view</li>
            </ul>
          </li>
        </ol>
      </section>

      {/* ========== 3 ========== */}
      <section className="guide-section">
        <div className="guide-section__heading">
          <span className="guide-section__badge">3</span>
          <span className="guide-section__heading-text">
            SETTING AREAS OF INTEREST
          </span>
        </div>

        <h4 className="guide-section__subtitle">Defining a New Area</h4>
        <ul className="guide-list">
          <li>
            Go to <b>Resource Access ▸ Areas of Interest (AOI)</b>.
          </li>
          <li>
            Click <b>Create AOI</b> to draw or select your analysis region. You
            can define areas by:
            <ul className="guide-list guide-list--nested">
              <li>Drawing on the map using polygon or rectangular tools</li>
              <li>Importing a GeoJSON boundary file</li>
              <li>Searching for a city, county, or custom coordinate range</li>
            </ul>
          </li>
        </ul>

        <h4 className="guide-section__subtitle">
          Customizing and Managing AOIs
        </h4>
        <ol className="guide-olist">
          <li>
            Once defined, name your AOI for easy reference (e.g., “Downtown Santa
            Monica Corridor”).
          </li>
          <li>
            You can set multiple AOIs within one project to compare different
            zones.
          </li>
          <li>
            Toggle each AOI layer on or off in the map view to focus on specific
            analysis areas.
          </li>
          <li>
            AOIs can be linked to datasets, connecting traffic data or emission
            results to a particular region.
          </li>
        </ol>
      </section>

      {/* ========== 4 ========== */}
      <section className="guide-section">
        <div className="guide-section__heading">
          <span className="guide-section__badge">4</span>
          <span className="guide-section__heading-text">
            GENERATING AND VIEWING RESULTS
          </span>
        </div>

        <h4 className="guide-section__subtitle">Running Your Analysis</h4>
        <ol className="guide-olist">
          <li>
            Once your data inputs, datasets, and AOIs are configured, navigate to{" "}
            <b>Analytics Tools</b> in the sidebar.
          </li>
          <li>
            Choose your preferred analysis mode:
            <ul className="guide-list guide-list--nested">
              <li>
                <b>Visual Analytics:</b> Generates visual maps, charts, and
                heatmaps showing factors such as traffic density, public transit
                efficiency, and emissions correlations.
              </li>
              <li>
                <b>Origin-Destination Analytics:</b> Examines movement patterns
                between key points or regions to understand flow efficiency and
                congestion impact.
              </li>
            </ul>
          </li>
          <li>
            Click <b>Run Analysis</b> and wait for processing to complete.
            Progress indicators show the current computation stage.
          </li>
        </ol>

        <h4 className="guide-section__subtitle">
          Viewing the Generated Reports
        </h4>
        <ol className="guide-olist">
          <li>
            Once completed, your results appear in the main interface with
            multiple visualization tabs.
          </li>
          <li>
            You can interact with:
            <ul className="guide-list guide-list--nested">
              <li>
                <b>Heatmaps and Flow Maps:</b> Show concentration and direction
                of traffic movement.
              </li>
              <li>
                <b>Emission or Mobility Charts:</b> Provide pollutant breakdowns
                or travel time distributions.
              </li>
              <li>
                <b>Trend Graphs:</b> Display how variables evolve across time
                intervals.
              </li>
            </ul>
          </li>
          <li>Each visualization supports zoom, layer toggling, and data export.</li>
        </ol>

        <h4 className="guide-section__subtitle">Exporting and Sharing Results</h4>
        <ul className="guide-list">
          <li>
            To export your analysis, select <b>Download Report</b> or{" "}
            <b>Export Visualization</b> from the results toolbar.
          </li>
          <li>Reports are available as PDFs, CSV summaries, or PNG images.</li>
          <li>
            For collaborative work, use the <b>Share Project</b> feature to invite
            teammates to view or comment on your results inside the platform.
          </li>
        </ul>
      </section>
    </div>
  );
}
